import { AxiosHeaders } from 'axios';
import { v4 as uuidv4 } from 'uuid';
const MULTIPART_REQUEST_CONFIG = {
    transformRequest: [
        (data, headers) => {
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
                delete headers['Content-Type'];
            }
            return data;
        },
    ],
};
/**
 * Uploads files to Agent Studio (`POST /files`), then posts user `messages` as separate text parts: prompt (if any),
 * serialized `data` (if non-empty), then `<file-med>` tags joined by newlines when multiple files upload
 * (`POST /sessions/messages`). Returns the last event’s last `content.parts[].text` and optional parsed JSON `data`
 * from the assistant reply.
 */
export async function sendPrompt(client, agentId, params) {
    const sessionId = params.sessionId ?? uuidv4();
    const prompt = params.prompt ?? '';
    const requestData = params.data ?? {};
    const files = params.files ?? [];
    const serializedData = Object.keys(requestData).length > 0 ? JSON.stringify(requestData) : '';
    const fileMedTags = files.length > 0 ? await uploadFiles(client, sessionId, agentId, files) : '';
    const messagesRes = await client.post('sessions/messages', {
        agent_id: agentId,
        session_id: sessionId,
        messages: [
            ...(prompt ? [{ type: 'text', text: prompt }] : []),
            ...(serializedData ? [{ type: 'text', text: serializedData }] : []),
            ...(fileMedTags ? [{ type: 'text', text: fileMedTags }] : []),
        ],
    });
    const { events } = messagesRes.data;
    if (!Array.isArray(events) || events.length === 0) {
        throw new Error('Agent Studio returned no events');
    }
    const lastEvent = events[events.length - 1];
    const text = getTextFromLastContentPart(lastEvent);
    const parsedAssistantData = parseDataFromAssistantText(text);
    return {
        text,
        ...(parsedAssistantData !== undefined ? { data: parsedAssistantData } : {}),
        nextSessionId: getNextSessionIdFromHeaders(messagesRes.headers) ?? sessionId,
    };
}
async function uploadFiles(client, sessionId, agentId, files) {
    const formData = new FormData();
    for (const file of files) {
        formData.append('files', file);
    }
    const search = new URLSearchParams();
    search.set('session_id', sessionId);
    search.set('agent_id', agentId);
    const uploadRes = await client.post(`files?${search.toString()}`, formData, MULTIPART_REQUEST_CONFIG);
    const uploaded = uploadRes.data.files ?? [];
    return uploaded
        .filter((f) => typeof f.id === 'string' && f.id.length > 0)
        .map((f) => `<file-med>${f.id}</file-med>`)
        .join('\n');
}
function getTextFromLastContentPart(lastEvent) {
    if (lastEvent === null || typeof lastEvent !== 'object') {
        return '';
    }
    const parts = lastEvent.content?.parts ?? [];
    if (parts.length === 0) {
        return '';
    }
    const lastPart = parts[parts.length - 1];
    const text = lastPart?.text;
    return typeof text === 'string' ? text : '';
}
function parseDataFromAssistantText(text) {
    if (!text.trim()) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(text);
        if (typeof parsed === 'object' && parsed !== null) {
            return parsed;
        }
        return undefined;
    }
    catch {
        return undefined;
    }
}
function getNextSessionIdFromHeaders(headers) {
    if (headers == null || typeof headers !== 'object') {
        return undefined;
    }
    if (headers instanceof AxiosHeaders) {
        const value = headers.get('x-session-id');
        return typeof value === 'string' && value.length > 0 ? value : undefined;
    }
    const record = headers;
    return ((typeof record['x-session-id'] === 'string' && record['x-session-id'].length > 0
        ? record['x-session-id']
        : undefined) ??
        (typeof record['X-Session-Id'] === 'string' && record['X-Session-Id'].length > 0
            ? record['X-Session-Id']
            : undefined));
}
